import { useEffect, useRef, useState } from "react";
import { practiceScenes } from "@/mocks/home";

export default function SectionPractice() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const passed = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? passed / total : 0;
      // 3 scenes
      const idx = Math.min(2, Math.floor(p * 3));
      setActive(idx);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section id="practice" className="relative bg-white">
      <div className="w-full px-6 md:px-10 pt-24 md:pt-32 pb-6">
        <div className="max-w-[1440px] mx-auto">
          <div className="text-ds-eyebrow text-ink-muted mb-space-ds-intro-eyebrow-heading">
            04 · Practice & Doctor
          </div>
          <h2 className="text-ds-section font-medium text-ink max-w-[640px]">
            The place, and the person behind the work.
          </h2>
        </div>
      </div>

      <div ref={wrapperRef} className="relative" style={{ height: "300vh" }}>
        <div className="sticky top-0 h-screen w-full flex items-center">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <div className="flex flex-col gap-6">
                  {practiceScenes.map((s, i) => {
                    const isActive = i === active;
                    return (
                      <div
                        key={s.id}
                        className={`transition-all duration-500 ${isActive ? "opacity-100" : "opacity-30"}`}
                      >
                        <div className="text-ds-eyebrow text-brand mb-space-ds-micro-label-value">{s.tag}</div>
                        <div className={`text-ds-content-title transition ${isActive ? "text-ink" : "text-ink-muted"}`}>
                          {s.title}
                        </div>
                        {isActive && (
                          <p className="mt-3 text-ds-body-sm text-ink-muted max-w-[440px]">{s.copy}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="relative w-full aspect-[16/10] rounded-editorial overflow-hidden bg-page-cool">
                  {practiceScenes.map((s, i) => (
                    <img
                      key={s.id}
                      src={s.image}
                      alt={s.title}
                      className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${i === active ? "opacity-100" : "opacity-0"}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead doctor tail */}
      <div className="w-full px-6 md:px-10 pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 border-t border-gray-200 pt-12">
          <div className="lg:col-span-4">
            <div className="w-full aspect-[4/5] rounded-editorial overflow-hidden bg-page-cool">
              <img
                src="https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20female%20dentist%20in%20clean%20clinical%20uniform%20standing%20in%20a%20premium%20private%20dental%20clinic%20with%20soft%20dental%20chair%20and%20overhead%20examination%20light%20visible%20behind%20her%2C%20natural%20calm%20confident%20expression%2C%20cool%20neutral%20professional%20lighting%2C%20believable%20clinical%20photography&width=800&height=1000&seq=doctor-ava&orientation=portrait"
                alt="Dr. Ava Aline"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-8 flex flex-col justify-between">
            <div>
              <div className="text-ds-eyebrow text-ink-muted mb-space-ds-profile-eyebrow-name">Lead Doctor</div>
              <div className="text-ds-profile-heading text-ink">Dr. Ava Aline</div>
              <div className="text-ds-meta text-ink-muted mt-1">Cosmetic & Restorative Dentistry · 15+ years</div>
              <p className="mt-6 text-ds-editorial-quote text-ink max-w-[640px]">
                <span className="font-serif-italic">"The best result shouldn't look like treatment."</span>
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-6 border-t border-gray-200 pt-8">
              <div>
                <div className="text-ds-eyebrow text-ink-muted mb-space-ds-micro-label-value">Education</div>
                <div className="text-ds-meta text-ink">DDS, University of Amsterdam<br/>MSc Prosthodontics, KCL London</div>
              </div>
              <div>
                <div className="text-ds-eyebrow text-ink-muted mb-space-ds-micro-label-value">Focus Areas</div>
                <div className="text-ds-meta text-ink">Porcelain veneers &middot; Digital smile design<br/>Full-mouth rehabilitation</div>
              </div>
              <div>
                <div className="text-ds-eyebrow text-ink-muted mb-space-ds-micro-label-value">Memberships</div>
                <div className="text-ds-meta text-ink">NVvP · Dutch Prosthodontics<br/>European Academy of Esthetic Dentistry</div>
              </div>
              <div>
                <div className="text-ds-eyebrow text-ink-muted mb-space-ds-micro-label-value">Languages</div>
                <div className="text-ds-meta text-ink">English · Dutch · German</div>
              </div>
              <div>
                <div className="text-ds-eyebrow text-ink-muted mb-space-ds-micro-label-value">Patients treated</div>
                <div className="text-ds-meta text-ink">3,200+ across 15 years<br/>from 40+ countries</div>
              </div>
              <div className="flex items-end">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 text-ds-meta text-ink border-b border-ink pb-0.5 hover:text-brand hover:border-brand transition cursor-pointer"
                >
                  Meet the Doctor <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}